export interface Category {
  slug: string;
  name: string;
  description: string;
  icon: string;
  image: string;
}

export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  image: string;
  content: string;
  featured: boolean;
}

export const categories: Category[] = [
  {
    slug: 'fashion',
    name: '时尚穿搭',
    description: '探索亚洲与欧美时尚风格的交融，从街头穿搭到高级定制，发现属于你的跨文化混搭灵感。',
    icon: 'ri-t-shirt-line',
    image: 'https://readdy.ai/api/search-image?query=Asian%20European%20fusion%20fashion%20style%20editorial%20photography%20model%20wearing%20mix%20of%20traditional%20Asian%20silk%20jacket%20with%20modern%20European%20tailoring%20clean%20warm%20cream%20background%20soft%20natural%20lighting%20minimalist%20aesthetic%20fashion%20magazine%20quality%20elegant%20composition%20warm%20neutral%20tones&width=800&height=500&seq=cat-fashion&orientation=landscape',
  },
  {
    slug: 'beauty',
    name: '美妆潮流',
    description: '对比亚洲与欧美美妆理念的差异与共通，掌握东西方妆容技巧，打造独一无二的融合妆容。',
    icon: 'ri-palette-line',
    image: 'https://readdy.ai/api/search-image?query=Asian%20European%20fusion%20beauty%20makeup%20concept%20half%20face%20with%20Korean%20glass%20skin%20natural%20makeup%20half%20face%20with%20European%20bold%20contour%20makeup%20artistic%20split%20composition%20soft%20warm%20studio%20lighting%20clean%20cream%20background%20editorial%20beauty%20photography%20elegant%20sophisticated%20warm%20earthy%20tones&width=800&height=500&seq=cat-beauty&orientation=landscape',
  },
  {
    slug: 'lifestyle',
    name: '生活方式',
    description: '从家居美学到旅行文化，从饮食哲学到日常仪式感，亚洲与欧美生活方式的碰撞与共鸣。',
    icon: 'ri-home-heart-line',
    image: 'https://readdy.ai/api/search-image?query=Asian%20European%20fusion%20lifestyle%20interior%20design%20room%20with%20Japanese%20minimalism%20mixed%20with%20Scandinavian%20hygge%20elements%20warm%20natural%20light%20through%20large%20windows%20indoor%20plants%20ceramic%20tea%20set%20on%20wooden%20table%20cozy%20yet%20elegant%20atmosphere%20editorial%20interior%20photography%20soft%20warm%20tones%20harmonious%20composition&width=800&height=500&seq=cat-lifestyle&orientation=landscape',
  },
];

export const articles: Article[] = [
  {
    slug: 'asian-european-street-style-2025',
    title: '2025 春夏街头：当东方极简遇上西方解构主义',
    excerpt: '本季街头最引人注目的趋势，莫过于东方极简美学与西方解构主义的深度对话。从东京到巴黎，新一代设计师正在重塑跨文化时装的语言。',
    category: 'fashion',
    author: '林晓雨',
    date: '2025-06-15',
    readTime: '8分钟',
    tags: ['街头时尚', '极简主义', '解构主义', '跨文化穿搭'],
    image: 'https://readdy.ai/api/search-image?query=Asian%20European%20street%20fashion%20fusion%202025%20model%20wearing%20oversized%20beige%20deconstructed%20blazer%20with%20Japanese%20origami%20folded%20details%20Tokyo%20street%20background%20soft%20warm%20morning%20light%20editorial%20fashion%20photography%20clean%20aesthetic%20warm%20neutral%20color%20palette%20elegant%20composition%20fashion%20magazine%20cover%20quality&width=800&height=500&seq=article-street-style&orientation=landscape',
    featured: true,
    content: `
<h2>东方极简美学的当代回归</h2>
<p>在2025年的时尚版图上，东方极简主义以一种前所未有的姿态回归。不同于上世纪90年代的日式极简浪潮，这一次的回归带着更深的哲学思考——它不再仅仅是剪裁上的减法，而是一种对空间、材质与身体关系的重新定义。</p>
<p>日本设计师山本耀司曾说过："黑色是一切颜色的尽头。"但2025年的东方极简主义告别了纯黑的统治，转向了更丰富的自然色调：米白、燕麦、烟灰、苔绿——这些源自自然界的色彩成为了新极简的核心语汇。</p>
<p>中国独立设计师品牌在这一季的表现尤为亮眼。他们从宋代美学的"留白"概念中汲取灵感，在服装结构上制造视觉"呼吸感"。不对称的下摆、层叠的薄纱、若隐若现的内衬——每一个细节都在诉说着"少即是多"的东方智慧。</p>
<h2>西方解构主义的新浪潮</h2>
<p>在大洋彼岸，欧洲设计师们正在经历一场解构主义的复兴。但与千禧年初Margiela式的激进解构不同，2025年的解构主义更加内敛和精致。设计师们不再追求视觉上的冲击力，而是在服装的内部结构上做文章。</p>
<p>巴黎世家在本季推出的一系列"内外翻转"西装外套引发了广泛讨论。这些看似常规的单品，实际上颠覆了传统西装的制作逻辑——内衬成为外饰，缝线成为装饰，口袋被重新定义位置。这种"隐秘的解构"恰好与东方美学中"藏"的概念不谋而合。</p>
<h2>跨文化混搭的三个核心法则</h2>
<p>法则一：<strong>材质对话</strong>。将日式棉麻的自然质感与意大利羊毛的精致结构并置。一件手工编织的亚麻上衣搭配剪裁利落的欧式西裤，材质的反差创造出独特的视觉张力。</p>
<p>法则二：<strong>比例游戏</strong>。东方服饰强调"宽袍大袖"的飘逸感，而西方剪裁注重贴合身形的精确度。将宽松的韩式廓形外套与紧身法式内搭相结合，在上宽下窄或上窄下宽的对比中寻找平衡点。</p>
<p>法则三：<strong>细节融合</strong>。不必全身混搭，有时一个细节就足够。在西式套装上点缀一枚中式盘扣，或在简约的日系穿搭中加入一条意式丝巾——点睛之笔往往比全盘混搭更有力量。</p>
<h2>街头穿搭实用指南</h2>
<p>如果你想要在日常穿搭中尝试这种跨文化风格，建议从基础单品开始。一条来自北欧品牌的阔腿裤，搭配一件带有中式立领细节的短上衣，再加上一双来自韩国设计师品牌的厚底乐福鞋——这就是一套完美的亚欧混搭出街造型。</p>
<p>配饰方面，可以选择一只法式腋下包搭配日式绳结腰带，或是一对意式耳环搭配中式发簪。关键在于让不同文化背景的单品在同一套造型中产生"对话"而非"争吵"。</p>
`,
  },
  {
    slug: 'k-beauty-vs-french-beauty',
    title: '韩式水光肌 vs 法式自然肌：两大美妆体系的终极对比',
    excerpt: 'K-Beauty追求完美无瑕的水光透亮，而法式美妆崇尚"不完美的优雅"。两种截然不同的美妆哲学背后，隐藏着怎样的文化密码？',
    category: 'beauty',
    author: '陈思怡',
    date: '2025-07-02',
    readTime: '10分钟',
    tags: ['韩式美妆', '法式美妆', '底妆对比', '护肤理念'],
    image: 'https://readdy.ai/api/search-image?query=Korean%20beauty%20glass%20skin%20vs%20French%20natural%20beauty%20makeup%20comparison%20two%20half%20faces%20artistic%20split%20portrait%20one%20side%20Korean%20dewy%20glowing%20perfect%20skin%20minimal%20makeup%20other%20side%20French%20effortless%20chic%20natural%20makeup%20studio%20lighting%20cream%20background%20editorial%20beauty%20photography%20warm%20tones%20elegant%20composition&width=800&height=500&seq=article-kbeauty-french&orientation=landscape',
    featured: true,
    content: `
<h2>两种文明的美丽叙事</h2>
<p>在美妆的世界里，韩国和法国代表了两种截然不同的审美哲学。如果说K-Beauty是一首精心编排的交响乐，那么法式美妆更像是一段即兴演奏的爵士——一个追求极致完美，一个拥抱自然缺陷。</p>
<p>这种差异并非偶然。它深深植根于两种文化的价值观、历史传统和社会心理之中。理解了这一点，我们才能真正理解为什么韩国女生会花40分钟完成一个"看起来像没化妆"的底妆，而法国女生真的可以在5分钟内完成全部妆容出门。</p>
<h2>K-Beauty：科学与仪式感的结合</h2>
<p>韩国美妆产业在过去十年间经历了爆炸式增长，其背后是韩国社会对"完美呈现"的强烈追求。在韩国，外貌管理被视为一种基本的社交礼仪和自我尊重的体现。</p>
<p>K-Beauty的核心在于"水光肌"——一种看起来饱满、透亮、仿佛从皮肤内部散发光泽的妆效。为了实现这一效果，韩国美妆品牌开发了极其精细的产品体系：从妆前乳到气垫粉底，从高光液到定妆喷雾，每一个步骤都是精心计算过的。</p>
<p><strong>韩式底妆的黄金公式</strong>：深层保湿 + 妆前打底 + 光泽气垫 + 局部提亮 + 透明定妆。关键在于"层层叠加但看起来毫不费力"——这需要大量的练习和对产品的精准把控。</p>
<h2>法式美妆：拥抱不完美的艺术</h2>
<p>法式美妆的精髓可以用一个词概括："Je ne sais quoi"——一种难以言喻的、不经意的魅力。法国女生化妆的核心目标不是"改变自己"，而是"强化自己"。她们乐于展示雀斑、细纹，甚至是轻微的黑眼圈，因为这些都是真实生活留下的痕迹。</p>
<p>典型的法式妆容极其简约：轻薄的BB霜或干脆不用粉底，只遮瑕局部瑕疵；一根黑色眼线笔；一层纤长睫毛膏；一支红色或裸色唇膏。整个流程不超过10分钟，但效果却散发着一种"我天生如此"的自信魅力。</p>
<h2>文化密码：集体主义 vs 个人主义</h2>
<p>从文化层面来看，K-Beauty的"追求完美"与韩国社会的集体主义传统密切相关。在一个高度重视群体评价和社会规范的社会里，外表管理是融入群体的重要方式。</p>
<p>而法式美妆的"拥抱个性"则是欧洲个人主义传统的自然延伸。在法国文化中，每个人都被鼓励发展独特的个人风格。盲目追随潮流被视为缺乏品味的表现——真正的优雅来自对自我的深刻认知。</p>
<h2>融合之道：打造属于你的跨文化妆容</h2>
<p>了解了两种体系的核心差异后，我们可以尝试将它们各自的长处结合起来，创造出最适合自己的妆容风格。</p>
<p>建议的融合方案：借鉴韩式护肤的精细流程打好底妆基础；用法式美妆的"减法思维"选择真正需要的彩妆步骤；在重点区域（颧骨、眉骨）采用韩式提亮技巧营造立体光泽；但保留法式的自然眉形和不完美的俏皮感。</p>
`,
  },
  {
    slug: 'wabi-sabi-meets-hygge',
    title: '侘寂遇见Hygge：当日本美学与丹麦幸福哲学同居',
    excerpt: '侘寂的不完美之美与Hygge的温暖舒适，看似矛盾的生活美学，在家居空间中却能碰撞出令人心安的归属感。',
    category: 'lifestyle',
    author: '王若涵',
    date: '2025-06-28',
    readTime: '7分钟',
    tags: ['侘寂', 'Hygge', '家居美学', '北欧设计'],
    image: 'https://readdy.ai/api/search-image?query=Wabi%20sabi%20meets%20Danish%20hygge%20interior%20design%20cozy%20living%20room%20with%20Japanese%20ceramic%20pottery%20on%20rustic%20wooden%20table%20soft%20candlelight%20linen%20textures%20warm%20neutral%20beige%20and%20cream%20tones%20natural%20light%20through%20paper%20screens%20minimalist%20yet%20cozy%20atmosphere%20editorial%20interior%20photography%20harmonious%20warm%20composition&width=800&height=500&seq=article-wabisabi-hygge&orientation=landscape',
    featured: true,
    content: `
<h2>两种生活美学的相遇</h2>
<p>日本侘寂（Wabi-sabi）和丹麦Hygge，是当今全球最受瞩目的两种生活美学。一个来自东方，一个来自北欧；一个崇尚不完美，一个追求舒适感。表面上看，它们似乎代表着不同的生活方式，但在深处，它们有着惊人的精神共鸣。</p>
<p>侘寂是一种源自禅宗思想的美学观，它接纳无常、不完美和不完整。一只带有裂纹的茶碗，一面长了青苔的石墙，一片飘落的樱花——这些看似"不完美"的存在，在侘寂的视角下都蕴含着深刻的美。</p>
<p>Hygge则是一种丹麦人独有的生活哲学，它强调温暖、舒适、亲密和当下的满足感。点一支蜡烛，裹着羊毛毯读一本好书，和好友分享一杯热巧克力——这些都是Hygge的经典场景。</p>
<h2>家居空间的融合实践</h2>
<p><strong>材质的选择</strong>：在侘寂与Hygge融合的空间中，自然材质是绝对的主角。未经打磨的原木、手工拉坯的陶器、粗粝的麻质布艺、温暖的羊毛织物——这些材质本身就承载着时间的痕迹和自然的温度。</p>
<p><strong>光影的营造</strong>：侘寂偏爱自然光的流动变化，Hygge痴迷于烛光的温暖摇曳。将两者结合，你可以通过大面积的窗户引入自然光，同时在角落布置多处柔和的灯光和烛光，让空间在不同时段呈现出不同的氛围。</p>
<p><strong>色彩的克制</strong>：统一采用大地色系——米白、砂色、浅灰、暖棕。这些低调的色彩让空间呈现出侘寂所需的宁静感，同时也为Hygge的温暖意提供了完美的底布。</p>
<h2>日常生活中的侘寂Hygge仪式</h2>
<p>早晨，用一个手工制作的日式茶碗泡一杯抹茶，坐在铺着羊毛垫的窗边，感受晨光洒落的宁静。午后，在一张带有天然木纹的餐桌上摆放简单的北欧式下午茶——全麦面包、丹麦黄油、手作果酱。傍晚，点燃几支蜂蜡蜡烛，在柔和的光线中翻阅一本旧书或写几行日记。</p>
<p>这种生活方式的核心在于：接纳不完美，珍惜当下，在日常中寻找美的瞬间。</p>
`,
  },
  {
    slug: 'cheongsam-reinvented',
    title: '旗袍新生：从东方符号到全球时尚语言的蜕变之路',
    excerpt: '旗袍不再只是东方文化的符号象征，全球设计师正在用当代视角重新诠释这件经典单品，让它成为跨文化衣橱的必备之选。',
    category: 'fashion',
    author: '林晓雨',
    date: '2025-05-20',
    readTime: '6分钟',
    tags: ['旗袍', '中式元素', '当代设计', '文化符号'],
    image: 'https://readdy.ai/api/search-image?query=Modern%20reinvented%20cheongsam%20qipao%20dress%20contemporary%20fashion%20reinterpretation%20with%20minimalist%20design%20model%20in%20cream%20studio%20mix%20of%20traditional%20Chinese%20silk%20fabric%20with%20modern%20European%20tailoring%20elegant%20editorial%20fashion%20photography%20warm%20natural%20lighting%20sophisticated%20composition%20soft%20earthy%20tones&width=800&height=500&seq=article-cheongsam&orientation=landscape',
    featured: false,
    content: `
<h2>旗袍的全球化叙事</h2>
<p>当Prada在2025春夏系列中推出一系列以旗袍为灵感的连衣裙时，全球时尚界再次将目光投向了这件承载着百年东方记忆的经典单品。但与以往不同的是，这一次的"旗袍复兴"不再是西方对东方的猎奇式想象，而是一场真正意义上的跨文化对话。</p>
<p>从上海裁缝铺到巴黎高定工坊，旗袍正在经历一场前所未有的当代蜕变。新一代设计师们不再满足于复刻传统旗袍的形制，而是将它视为一种可被解构、重组、再创造的时尚语汇。</p>
<h2>当代设计师的创新演绎</h2>
<p>中国设计师Uma Wang将旗袍的立领元素与oversized西式廓形相结合，创造出一种兼具东方韵味和现代感的全新单品。她的设计打破了旗袍"紧身"的刻板印象，赋予穿着者更多的身体自由度。</p>
<p>与此同时，韩国品牌Wooyoungmi则将旗袍的侧开叉细节融入到了西装裤的设计中，在利落的西方剪裁中注入了一抹东方的含蓄性感。这种"细节混搭"的方式为日常穿搭提供了更多的可能性。</p>
<h2>搭配指南：让旗袍融入日常衣橱</h2>
<p>如果你想要在日常穿搭中尝试旗袍元素，建议从改良款入手。选择一件及膝长度的A字廓形旗袍上衣，搭配牛仔裤和白色运动鞋，就能轻松打造出既有个性又不突兀的日常造型。</p>
`,
  },
  {
    slug: 'sunscreen-east-west',
    title: '防晒文化大不同：亚洲"严防死守" vs 欧美"适度享受"',
    excerpt: '同样是防晒，亚洲人追求极致的物理防护和美白效果，而欧美人更注重维生素D的摄入和自然的古铜肤色。两种观念的碰撞带给我们什么启示？',
    category: 'beauty',
    author: '陈思怡',
    date: '2025-07-10',
    readTime: '9分钟',
    tags: ['防晒', '护肤文化', '美白', '维生素D'],
    image: 'https://readdy.ai/api/search-image?query=Sunscreen%20comparison%20concept%20East%20vs%20West%20artistic%20still%20life%20photography%20Asian%20sunscreen%20products%20with%20parasol%20and%20UV%20protective%20clothing%20on%20one%20side%20European%20sunscreen%20oil%20with%20beach%20accessories%20on%20other%20side%20split%20composition%20warm%20natural%20light%20cream%20background%20editorial%20beauty%20photography%20soft%20warm%20tones&width=800&height=500&seq=article-sunscreen&orientation=landscape',
    featured: false,
    content: `
<h2>一分为二的防晒世界观</h2>
<p>如果你曾在夏天去过亚洲和欧洲的海滩，你一定会注意到一个有趣的现象：亚洲海滩上，遮阳伞、防晒衣、宽檐帽和冰袖是标配；而欧洲海滩上，人们正在享受阳光的沐浴，时不时补涂一层助晒油。</p>
<p>这不仅仅是防晒习惯的差异，更是两种文化对"美"和"健康"截然不同的定义。</p>
<h2>亚洲防晒：一场全民参与的护肤战争</h2>
<p>在东亚国家，"一白遮百丑"的审美观念根深蒂固。防晒不仅仅是为了防止晒伤，更是一种美白策略。亚洲防晒产品以其极致的使用体验著称——轻薄、不黏腻、不影响后续上妆，同时SPF值动辄50+。</p>
<p>韩国和日本的防晒技术在全球处于领先地位。从化学防晒到物理防晒，从日常通勤到户外运动，亚洲品牌为每一种场景都开发了专门的防晒产品。</p>
<h2>欧美防晒：在保护与享受之间寻找平衡</h2>
<p>相比之下，欧美消费者对待防晒的态度更为"随性"。虽然皮肤癌的预防意识在美国和澳大利亚等国非常强烈，但在南欧，"健康的古铜肤色"仍然是许多人心目中的理想肤色。</p>
<p>值得注意的是，近年来随着亚洲美妆文化在全球的传播，越来越多的欧美消费者开始重视日常防晒。欧美品牌也开始借鉴亚洲防晒产品的轻盈质地和多功能配方。</p>
<h2>科学视角：什么才是最优的防晒策略</h2>
<p>从皮肤科医学的角度来看，亚洲式的全面防晒策略确实能更有效地预防光老化和皮肤癌。但完全避光也可能导致维生素D缺乏。理想的防晒策略应当是：日常使用广谱防晒产品，但在清晨或傍晚适度接受阳光照射，以确保维生素D的充足合成。</p>
`,
  },
  {
    slug: 'tea-ceremony-coffee-ritual',
    title: '茶道与咖啡仪式：东西方饮品文化中的生活哲学',
    excerpt: '日本茶道的"一期一会"与意大利咖啡文化的"慢生活"，两种饮品仪式承载着不同文明对时间、社交和自我的理解。',
    category: 'lifestyle',
    author: '王若涵',
    date: '2025-06-10',
    readTime: '8分钟',
    tags: ['茶道', '咖啡文化', '生活仪式', '饮食品鉴'],
    image: 'https://readdy.ai/api/search-image?query=Japanese%20tea%20ceremony%20meets%20Italian%20coffee%20ritual%20artistic%20still%20life%20composition%20matcha%20bowl%20with%20bamboo%20whisk%20on%20left%20espresso%20machine%20with%20ceramic%20cup%20on%20right%20warm%20natural%20lighting%20minimalist%20cream%20background%20editorial%20food%20photography%20harmonious%20warm%20tones%20sophisticated%20composition&width=800&height=500&seq=article-tea-coffee&orientation=landscape',
    featured: false,
    content: `
<h2>饮品的文明叙事</h2>
<p>在人类文明的长河中，没有哪种日常行为比"饮用"更能反映一个文化的深层价值观。茶和咖啡，作为东西方最具代表性的饮品，不仅是味觉的享受，更是一种生活哲学的物化表达。</p>
<p>日本茶道中的"一期一会"——珍惜每一次相遇的独特性；意大利咖啡文化中的"prendere un caffè"——在吧台前用30秒喝完一杯浓缩咖啡，却在此过程中完成了最重要的社交仪式。两种看似截然不同的饮用方式，却都在诉说着同一个道理：活在当下。</p>
<h2>空间与仪式的对比</h2>
<p>日本茶室是一个刻意与世俗隔离的"结界"。低矮的入口强迫每一位进入者俯身，象征放下身份与傲慢。茶室的每一个细节——插花、挂轴、茶碗的朝向——都经过精心设计，引导参与者进入冥想般的专注状态。</p>
<p>意大利咖啡馆则是一个完全开放的社交舞台。吧台前此起彼伏的交谈声、咖啡机蒸汽的嘶嘶声、杯碟碰撞的清脆声响——这一切构成了意大利城市生活的背景音。在这里，咖啡是社交的润滑剂，而非冥想的媒介。</p>
<h2>融合：在家创造你的跨文化饮品仪式</h2>
<p>不妨尝试将两种文化的饮品智慧融入你的日常生活。早晨，用一杯意式浓缩咖啡快速唤醒身体，在吧台般的厨房操作台前站立饮用，感受那份意大利式的效率与活力。午后，为自己准备一席"简式茶道"——用一把心爱的紫砂壶，按工夫茶的步骤冲泡一壶铁观音，在十分钟的专注中暂停快节奏的生活。</p>
`,
  },
  {
    slug: 'genderless-fashion-asia-europe',
    title: '无性别穿搭：亚洲柔美与欧洲刚毅的破界之旅',
    excerpt: '当韩流男星的柔美妆容遇见北欧的极简无性别设计，性别边界的模糊正在重塑整个时尚产业的底层逻辑。',
    category: 'fashion',
    author: '林晓雨',
    date: '2025-04-18',
    readTime: '7分钟',
    tags: ['无性别时尚', '性别流动', '韩流美学', '北欧设计'],
    image: 'https://readdy.ai/api/search-image?query=Genderless%20fashion%20Asia%20Europe%20fusion%20androgynous%20model%20wearing%20oversized%20neutral%20blazer%20with%20soft%20Korean%20style%20silk%20blouse%20minimalist%20Scandinavian%20design%20elements%20clean%20cream%20studio%20background%20editorial%20fashion%20photography%20warm%20natural%20lighting%20sophisticated%20composition%20soft%20warm%20earth%20tones&width=800&height=500&seq=article-genderless&orientation=landscape',
    featured: false,
    content: `
<h2>性别边界的消融</h2>
<p>在2025年的时尚舞台上，性别不再是一条泾渭分明的分界线，而是一个可以自由流动的光谱。亚洲和欧洲的无性别时尚运动虽然都指向同一个方向——打破服装的性别桎梏——但它们的表达方式却各具特色。</p>
<h2>亚洲路径：柔美力量的崛起</h2>
<p>亚洲的无性别时尚运动很大程度上是由K-pop文化推动的。从BTS到Blackpink，韩国偶像们以其突破传统的着装风格，重新定义了"男性气质"和"女性气质"的边界。男偶像画着眼线、涂抹唇彩、穿着透视装和蕾丝；女偶像则以西装和工装造型展现"Girl Crush"的魅力。</p>
<h2>欧洲路径：极简主义的去性别化</h2>
<p>北欧品牌在无性别设计领域走在前列。瑞典品牌Acne Studios和COS以其简约、中性的设计语言，有意模糊了传统男女装的界限。他们的设计思路不是"让男性穿女性化的衣服"，而是创造出一种超越性别分类的全新服装品类。</p>
<h2>融合穿搭指南</h2>
<p>打造无性别混搭造型的关键在于比例的平衡。一件来自韩国设计师品牌的 oversized 丝绸衬衫（柔美元素），搭配一条来自北欧品牌的直筒工装裤（刚毅元素），再配上一双中性设计的厚底皮鞋——这套造型模糊了所有传统的性别标签。</p>
`,
  },
  {
    slug: 'serum-routine-hybrid',
    title: '精华液东西方混搭法：打造专属你的跨文化护肤流程',
    excerpt: '韩国的"七层精华法"与法国的"少即是多"护肤理念，看似水火不容，实则可以取长补短，创造最优化的个人护肤方案。',
    category: 'beauty',
    author: '陈思怡',
    date: '2025-05-08',
    readTime: '6分钟',
    tags: ['精华液', '护肤流程', '韩式护肤', '法式护肤'],
    image: 'https://readdy.ai/api/search-image?query=East%20West%20skincare%20serum%20routine%20fusion%20minimalist%20arrangement%20of%20Korean%20glass%20skin%20products%20with%20French%20pharmacy%20skincare%20on%20marble%20tray%20fresh%20flowers%20soft%20natural%20lighting%20cream%20background%20editorial%20beauty%20photography%20warm%20elegant%20tones%20sophisticated%20composition&width=800&height=500&seq=article-serum&orientation=landscape',
    featured: false,
    content: `
<h2>护肤理念的东西方碰撞</h2>
<p>韩国"十步护肤法"曾风靡全球，而其核心灵魂——精华液的多层叠加——更是被无数护肤爱好者奉为圭臬。与之形成鲜明对比的是，法式护肤向来崇尚"少即是多"，一个药妆品牌的保湿霜可能就是法国女生全部的护肤投资。</p>
<p>两种截然不同的护肤哲学，到底哪一种更有效？答案可能是：取两者之长，打造属于你自己的"第三条道路"。</p>
<h2>韩式精华叠加法的科学性</h2>
<p>韩式护肤的核心逻辑是"针对性解决"——不同的精华液针对不同的肌肤问题，通过层层叠加实现全面护理。从水前精华到功能性精华再到保湿精华，每一步都有明确的功效目标。</p>
<h2>法式精简护肤的智慧</h2>
<p>法式护肤的哲学基础是"尊重皮肤的自我调节能力"。法国女生相信，过度干预反而可能破坏皮肤屏障。因此她们的护肤流程通常极其简单：清洁、保湿、防晒——仅此而已。</p>
<h2>跨文化混搭方案</h2>
<p>推荐的融合之道：采用法式的"尊重肌肤"理念作为基础，辅以韩式的"精准护理"策略。早晚护肤以轻薄精简为主（法式思维），但根据季节和肌肤状态，每周2-3次进行2-3层精华的叠加护理（韩式智慧）。</p>
`,
  },
  {
    slug: 'minimalist-wardrobe-fusion',
    title: '胶囊衣橱的跨文化升级：日式收纳智慧 + 法式穿搭哲学',
    excerpt: '日本收纳女王近藤麻理惠的"心动法则"遇上法式胶囊衣橱的精简哲学，一场关于少而精的跨文化对话。',
    category: 'lifestyle',
    author: '王若涵',
    date: '2025-04-25',
    readTime: '5分钟',
    tags: ['胶囊衣橱', '极简生活', '收纳整理', '法式穿搭'],
    image: 'https://readdy.ai/api/search-image?query=Minimalist%20capsule%20wardrobe%20Japan%20meets%20France%20organized%20open%20closet%20with%20neutral%20color%20clothing%20Japanese%20folding%20method%20displayed%20with%20French%20chic%20accessories%20warm%20natural%20light%20from%20window%20cream%20interior%20editorial%20home%20photography%20soft%20warm%20tones%20elegant%20organized%20composition&width=800&height=500&seq=article-wardrobe&orientation=landscape',
    featured: false,
    content: `
<h2>少即是多的东西方共识</h2>
<p>日本的"断舍离"和法国的"less is more"，虽然来自完全不同的文化土壤，却在"精简生活"这一点上达成了惊人的共识。当我们将日本的收纳智慧与法国的穿搭哲学结合起来，就能构建出一个兼具功能性和美感的理想衣橱。</p>
<h2>日式收纳：让每一件衣物都被看见</h2>
<p>近藤麻理惠的"KonMari"整理法在全球拥有数以亿计的追随者，其核心信条——"只留下让你心动的物品"——为衣橱管理提供了强大的决策框架。而日式折叠法（将衣物竖立放置）则解决了"看不见就会忘记"的收纳难题。</p>
<h2>法式穿搭：用更少的单品创造更多的可能</h2>
<p>法式胶囊衣橱的精髓在于"投资基础款"。一件剪裁完美的白衬衫、一条合身的深色牛仔裤、一件质地优良的羊绒毛衣——这些看似普通的单品，通过巧妙的搭配可以创造出无数种不同的造型。</p>
<h2>融合方案</h2>
<p>第一步：用法式的"基础款清单"确定衣橱的核心骨架。第二步：用日式的"心动法则"筛选每一件单品。第三步：用日式折叠法将衣物竖立收纳，确保每一件都能被轻松找到和取用。第四步：定期用法式的"搭配实验"发掘现有单品的新可能性，避免不必要的购买冲动。</p>
`,
  },
];

export const teamMembers = [
  {
    name: '林晓雨',
    role: '创始人 & 时尚主编',
    bio: '曾在巴黎ESMOD深造，后旅居东京三年。致力于搭建东西方时尚文化的桥梁。',
    image: 'https://readdy.ai/api/search-image?query=Asian%20female%20fashion%20editor%20portrait%20elegant%20modern%20woman%20in%20beige%20blazer%20warm%20natural%20lighting%20cream%20studio%20background%20professional%20editorial%20portrait%20photography%20sophisticated%20warm%20tones%20confident%20yet%20approachable%20expression&width=400&height=500&seq=team-lin&orientation=portrait',
  },
  {
    name: '陈思怡',
    role: '美妆专栏总监',
    bio: '前韩国爱茉莉太平洋集团产品开发专家，拥有十年跨文化美妆研究经验。',
    image: 'https://readdy.ai/api/search-image?query=Asian%20female%20beauty%20editor%20portrait%20professional%20woman%20in%20soft%20sage%20green%20blouse%20warm%20natural%20lighting%20cream%20studio%20background%20editorial%20portrait%20photography%20sophisticated%20warm%20tones%20warm%20smile%20elegant%20composition&width=400&height=500&seq=team-chen&orientation=portrait',
  },
  {
    name: '王若涵',
    role: '生活方式版主编',
    bio: '旅居北欧五年，深谙极简生活美学。致力于将东西方生活智慧融为一体。',
    image: 'https://readdy.ai/api/search-image?query=Asian%20female%20lifestyle%20editor%20portrait%20creative%20woman%20in%20warm%20neutral%20knitwear%20soft%20natural%20lighting%20cream%20studio%20background%20editorial%20portrait%20photography%20sophisticated%20warm%20tones%20gentle%20expression%20harmonious%20composition&width=400&height=500&seq=team-wang&orientation=portrait',
  },
];

export const testimonials = [
  {
    name: '张雨婷',
    location: '上海',
    text: '这个网站的跨文化视角太独特了！我之前一直觉得亚洲和欧美风格是互斥的，但读了那篇旗袍新生的文章后，终于找到了把它们融合在一起的方法。现在我的穿搭终于不再"非此即彼"了。',
    rating: 5,
  },
  {
    name: '李思涵',
    location: '伦敦',
    text: '作为一个生活在欧洲的亚洲女生，这个网站完美解答了我对两种文化身份的困惑。特别是防晒文化对比那篇文章，让我明白了自己的护肤习惯背后竟然有这么多文化因素。',
    rating: 5,
  },
  {
    name: 'Chris Müller',
    location: '柏林',
    text: '虽然我是德国人，但我对亚洲文化非常着迷。通过这个网站，我学到了很多将亚洲设计元素融入日常生活的方法。现在我的公寓就是一个侘寂与北欧风的完美结合体。',
    rating: 5,
  },
];